import React from 'react';
import { Card, CardHeader, CardBody } from './Card';

/**
 * SectionCardHeader — canonical header shape used by SectionCard AND by any
 * section that owns its own <Card> (or none). Split out from SectionCard on
 * 2026-07-31 (§3.1 SectionHeader wave, supersedes rev-5 Customer-Types-only
 * scoping — see iter18-settings-polish-round-2 §0.4 supersession footnote).
 *
 * Shape:
 *   [icon]  Title (h3, text-sm semibold)   + right-aligned `actions` slot
 *           description (p, text-xs muted, NOT bold)
 *
 * When to use which:
 *   - SectionCard             — need a <Card> AND the header shape (icon+desc+actions). Body is your list.
 *   - SectionCardHeader alone — you already own the <Card> (or want no surface). Use inside your own CardHeader slot.
 *   - SectionHeader (REQ-COMP-06, ui/SectionHeader.js) — need a count-badge next to the title; no icon.
 *
 * Props:
 *   title       string | ReactNode — required
 *   description string — muted subtitle under the title, optional
 *   icon        heroicon component (Icon, not element) — optional; renders left of title
 *   actions     ReactNode — right-aligned in the header row
 *   className   optional wrapper className (rare; the surrounding Card usually owns layout)
 */
export function SectionCardHeader({ title, description, icon: Icon, actions, className }) {
  return (
    <CardHeader
      className={`flex items-start justify-between gap-3${className ? ` ${className}` : ''}`}
      // Wrap `actions` in a flex-shrink-0 whitespace-nowrap container so long
      // add-button labels (e.g. "Add customer type", "Add work order type") do
      // not wrap to 2-3 lines when the row width is constrained. Pre-existing
      // issue on the reference page (CustomerTypes) surfaced when the pattern
      // propagated to 5 more pages — fix here inoculates all 12 mounts.
      action={actions ? <div className="flex-shrink-0 whitespace-nowrap">{actions}</div> : undefined}
    >
      <div className="flex items-start gap-2 min-w-0">
        {Icon && (
          <Icon
            className="h-4 w-4 flex-shrink-0 mt-0.5"
            style={{ color: 'var(--color-text-muted)' }}
            aria-hidden="true"
          />
        )}
        <div className="min-w-0">
          <h3
            className="text-sm font-semibold truncate"
            style={{ color: 'var(--color-text-default)' }}
          >
            {title}
          </h3>
          {description && (
            <p
              className="text-xs mt-0.5 font-normal"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {description}
            </p>
          )}
        </div>
      </div>
    </CardHeader>
  );
}

/**
 * SectionCard — canonical stacked-list section wrapper.
 *
 * Extracted from JobWOTypesSettings' `TypeSection` (Iter18 Polish Round 2 §1.4).
 *
 * Rev-5 (2026-07-17) scoped its use to Customer Types only. That scoping was a
 * Round-2 pacing decision, not a structural boundary. SUPERSEDED 2026-07-31 by
 * operator (§3.1 SectionHeader wave, "build components not pages" per DEC-48):
 * the pattern now applies to all six settings surfaces that carry section
 * headers — Customer Types (reference), Competitors + Lost Reasons, Jobs & WO
 * Types + Cancellation Reasons, Vendors + Warehouses, Notifications, Help &
 * Support. See iter18-settings-polish-round-2.md §0.4 supersession footnote.
 *
 * Consumers (6 pages, 12+ mount points): registered in
 * `Portica Clients/IDI/BIDIT/Specifications/COMPONENT_MAP.md`.
 *
 * Composition: SectionCard = <Card> + <SectionCardHeader> + <CardBody p-0>.
 * If your page already owns its <Card> (or wants no surface), import
 * `SectionCardHeader` directly and skip the wrapper.
 *
 * Props: same as SectionCardHeader, plus:
 *   children   list rows / body content (rendered inside CardBody p-0)
 *   className  passed through to the outer <Card>
 */
export default function SectionCard({ title, description, icon, actions, children, className }) {
  return (
    <Card className={className}>
      <SectionCardHeader
        title={title}
        description={description}
        icon={icon}
        actions={actions}
      />
      <CardBody className="p-0">{children}</CardBody>
    </Card>
  );
}
